import { useEffect, useMemo, useState } from 'react'
import { Save, X, Loader2, ChevronDown, ChevronUp } from 'lucide-react'
import { useUpdateSeries } from '../../api/series'
import { useAuth } from '../../hooks/use-auth'
import { isValidEpisodeDuration, DEFAULT_EPISODE_DURATION_MINUTES } from '../../lib/plans'
import { useVoices } from '../../api/voices'
import { VoiceSelect } from '../../features/podcast-editor/components/voice-select/voice-select'
import type { VoiceOption } from '../../features/podcast-editor/context/podcast-editor-provider'
import { LanguagePicker } from '../language-picker'
import type { Series, UpdateSeriesRequest } from '../../types/series'

interface SeriesSettingsPanelProps {
  series: Series
  onClose: () => void
}

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
]

const STYLE_OPTIONS = [
  { value: 'morning-briefing', label: 'Morning Briefing' },
  { value: 'two-friends', label: 'Two Friends' },
  { value: 'deep-dive', label: 'Deep Dive' },
  { value: 'storyteller', label: 'Storyteller' },
]

const DURATION_OPTIONS = [10, 20, 30]

export function SeriesSettingsPanel({ series, onClose }: SeriesSettingsPanelProps) {
  const updateSeries = useUpdateSeries()
  const { user } = useAuth()
  const userPlanDuration = isValidEpisodeDuration(user?.episodeDurationMinutes)
    ? user!.episodeDurationMinutes!
    : DEFAULT_EPISODE_DURATION_MINUTES
  const [showAdvanced, setShowAdvanced] = useState(false)

  const [form, setForm] = useState({
    name: series.name,
    description: series.description || '',
    interests: series.interests,
    negativeKeywords: series.negativeKeywords || '',
    frequency: series.frequency,
    episodeDurationMinutes: series.episodeDurationMinutes,
    deliveryTimeLocal: series.deliveryTimeLocal,
    timezone: series.timezone,
    style: series.style,
    hostAVoiceId: series.hostAVoiceId || '',
    hostBVoiceId: series.hostBVoiceId || '',
    languageIso: series.languageIso || 'en',
  })

  // Filter voices by the currently selected language so users only see voices that
  // actually speak that language. Re-runs reactively whenever the language changes.
  const { data: allVoices, isLoading: loadingVoices } = useVoices(undefined, form.languageIso)

  const voiceOptions: VoiceOption[] = useMemo(() => {
    if (!allVoices) return []
    return allVoices.map(v => ({
      id: v.id,
      name: v.name,
      description: v.description ?? undefined,
      gender: v.gender ?? undefined,
      previewUrl: v.previewUrl ?? undefined,
      category: v.category,
    }))
  }, [allVoices])

  // When the language changes, reset host selections that are no longer valid for the
  // newly filtered voice list. Auto-pick a masculine/feminine pair when possible.
  useEffect(() => {
    if (loadingVoices || voiceOptions.length === 0) return

    const hasA = form.hostAVoiceId !== '' && voiceOptions.some((v) => v.id === form.hostAVoiceId)
    const hasB = form.hostBVoiceId !== '' && voiceOptions.some((v) => v.id === form.hostBVoiceId)
    if (hasA && hasB) return

    const masculine = voiceOptions.find((v) => v.gender?.toLowerCase() === 'masculine')
    const feminine = voiceOptions.find((v) => v.gender?.toLowerCase() === 'feminine')
    const fallbackA = masculine ?? voiceOptions[0]
    const fallbackB = feminine && feminine.id !== fallbackA.id
      ? feminine
      : voiceOptions.find((v) => v.id !== fallbackA.id) ?? fallbackA

    setForm((prev) => ({
      ...prev,
      hostAVoiceId: hasA ? prev.hostAVoiceId : fallbackA.id,
      hostBVoiceId: hasB ? prev.hostBVoiceId : fallbackB.id,
    }))
  }, [voiceOptions, loadingVoices, form.hostAVoiceId, form.hostBVoiceId])

  const isDirty =
    form.name !== series.name ||
    form.description !== (series.description || '') ||
    form.interests !== series.interests ||
    form.negativeKeywords !== (series.negativeKeywords || '') ||
    form.frequency !== series.frequency ||
    form.episodeDurationMinutes !== series.episodeDurationMinutes ||
    form.deliveryTimeLocal !== series.deliveryTimeLocal ||
    form.timezone !== series.timezone ||
    form.style !== series.style ||
    form.hostAVoiceId !== (series.hostAVoiceId || '') ||
    form.hostBVoiceId !== (series.hostBVoiceId || '') ||
    form.languageIso !== (series.languageIso || 'en')

  const handleSave = async () => {
    const payload: UpdateSeriesRequest & { id: string } = {
      id: series.id,
      name: form.name,
      description: form.description || undefined,
      interests: form.interests,
      negativeKeywords: form.negativeKeywords || undefined,
      frequency: form.frequency,
      episodeDurationMinutes: form.episodeDurationMinutes,
      deliveryTimeLocal: form.deliveryTimeLocal,
      timezone: form.timezone,
      style: form.style,
      hostAVoiceId: form.hostAVoiceId || undefined,
      hostBVoiceId: form.hostBVoiceId || undefined,
      languageIso: form.languageIso || undefined,
    }

    try {
      await updateSeries.mutateAsync(payload)
      onClose()
    } catch {
      // error is exposed via updateSeries.error
    }
  }



  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white p-6">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Settings</h3>
          <button
            onClick={onClose}
            className="cursor-pointer rounded-lg p-1.5 text-foreground/40 transition-colors hover:bg-black/5 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5">
          {/* Name */}
          <Field label="Name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
            />
          </Field>

          {/* Description */}
          <Field label="Description">
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
            />
          </Field>

          {/* Interests */}
          <Field label="Interests">
            <textarea
              value={form.interests}
              onChange={(e) => setForm({ ...form, interests: e.target.value })}
              rows={3}
              className="w-full resize-none rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
            />
          </Field>

          {/* Schedule row */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Frequency">
              <select
                value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value as any })}
                className="w-full cursor-pointer rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Duration">
              <select
                value={form.episodeDurationMinutes}
                onChange={(e) => setForm({ ...form, episodeDurationMinutes: parseInt(e.target.value) })}
                className="w-full cursor-pointer rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              >
                {DURATION_OPTIONS.map((d) => (
                  <option key={d} value={d} disabled={d > userPlanDuration}>
                    {d} min{d > userPlanDuration ? ' (upgrade required)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Style */}
          <Field label="Style">
            <select
              value={form.style}
              onChange={(e) => setForm({ ...form, style: e.target.value as any })}
              className="w-full cursor-pointer rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
            >
              {STYLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          {/* Voice & language */}
          <div className="space-y-3">
            <VoiceSelect
              label="Host A voice"
              value={form.hostAVoiceId}
              onChange={(id) => setForm({ ...form, hostAVoiceId: id })}
              options={voiceOptions}
              loading={loadingVoices}
              labelClassName="text-xs font-medium text-foreground/60"
            />
            <VoiceSelect
              label="Host B voice"
              value={form.hostBVoiceId}
              onChange={(id) => setForm({ ...form, hostBVoiceId: id })}
              options={voiceOptions}
              loading={loadingVoices}
              labelClassName="text-xs font-medium text-foreground/60"
            />
            <Field label="Language">
              <LanguagePicker
                value={form.languageIso}
                onChange={(iso) => setForm({ ...form, languageIso: iso })}
              />
            </Field>
            <p className="text-xs text-foreground/40">Changes apply to future episodes only.</p>
          </div>

          {/* Advanced toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex w-full cursor-pointer items-center gap-1.5 text-xs font-medium text-foreground/40 transition-colors hover:text-foreground/60"
          >
            {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Advanced settings
          </button>

          {showAdvanced && (
            <div className="space-y-5">
              {/* Negative keywords */}
              <Field label="Exclude topics">
                <textarea
                  value={form.negativeKeywords}
                  onChange={(e) => setForm({ ...form, negativeKeywords: e.target.value })}
                  rows={2}
                  placeholder="Topics to exclude..."
                  className="w-full resize-none rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </Field>

              {/* Delivery time */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Delivery time">
                  <input
                    type="time"
                    value={`${String(Math.floor(form.deliveryTimeLocal / 60)).padStart(2, '0')}:${String(form.deliveryTimeLocal % 60).padStart(2, '0')}`}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(':').map(Number)
                      setForm({ ...form, deliveryTimeLocal: h * 60 + m })
                    }}
                    className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </Field>

                <Field label="Timezone">
                  <input
                    type="text"
                    value={form.timezone}
                    onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                    className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-foreground/60 outline-none transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                    readOnly
                  />
                </Field>
              </div>
            </div>
          )}
        </div>

        {/* Save / Error */}
        {updateSeries.error && (
          <div className="mt-4 rounded-lg bg-danger-50 px-3 py-2 text-xs text-danger-700">
            Failed to save settings. Please try again.
          </div>
        )}

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={() => void handleSave()}
            disabled={!isDirty || updateSeries.isPending}
            className="flex cursor-pointer items-center gap-2 rounded-xl bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {updateSeries.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save changes
          </button>
          <button
            onClick={onClose}
            className="cursor-pointer rounded-xl px-4 py-2 text-sm text-foreground/60 transition-colors hover:bg-black/5"
          >
            Cancel
          </button>
        </div>
      </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-foreground/60">{label}</label>
      {children}
    </div>
  )
}
